/**
 * Shared KPI value formatters.
 *
 * Extracted from KpiDashboardTab so the trend chart formats identically to the
 * tiles beside it — a chart that rounded differently from the tile above it
 * would read as two disagreeing numbers.
 */

export type MetricFormat = 'money' | 'number' | 'decimal' | 'percent';

export function formatMetric(
  value: number | null | undefined,
  format: MetricFormat,
): string {
  switch (format) {
    case 'money':
      return money(value);
    case 'number':
      return number(value);
    case 'decimal':
      return decimal(value);
    case 'percent':
      return pct(value);
    default:
      return 'n/a';
  }
}

export function money(value: number | null | undefined) {
  if (value == null) return 'n/a';
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function number(value: number | null | undefined) {
  if (value == null) return 'n/a';
  return Math.round(value).toLocaleString();
}

export function decimal(value: number | null | undefined) {
  if (value == null) return 'n/a';
  return value.toLocaleString(undefined, {
    maximumFractionDigits: 1,
  });
}

export function pct(value: number | null | undefined) {
  if (value == null) return 'n/a';
  return `${(value * 100).toFixed(1)}%`;
}

export function clamp01(value: number) {
  if (Number.isNaN(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Render a period-over-period delta.
 *
 * ⚠ The argument is a FRACTION, not percentage points. The backend computes
 * `percent = absolute / previous_f` (kpi_diagnostics_service.py) — a doubling
 * is 1.0, not 100 — and its own test pins that. This function used to print
 * that number straight through with a "%" suffix, so a real +18.7% change
 * rendered as "+0.2%" and -31.2% as "-0.3%": every delta on the dashboard read
 * ~100x too small, while the backend's narrative on the SAME page formatted
 * the same field correctly (`_format_pct` = value * 100). Found by Art,
 * 2026-09-22.
 *
 * `pct()` above already scales the same way; these two were the only percent
 * formatters in this file and they disagreed.
 */
export function formatPercentDelta(fraction: number) {
  const points = fraction * 100;
  const sign = points > 0 ? '+' : '';
  return `${sign}${points.toFixed(1)}%`;
}

/**
 * Compact form for axis ticks, where the full `money`/`number` output would
 * collide with its neighbours. Tick text only — never a headline value, which
 * should stay exact.
 */
export function compactTick(
  value: number | null | undefined,
  format: MetricFormat,
): string {
  if (value == null) return '';
  if (format === 'percent') return `${(value * 100).toFixed(1)}%`;

  const prefix = format === 'money' ? '$' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${prefix}${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${prefix}${(value / 1_000).toFixed(1)}k`;
  // Sub-1 money (a $0.42 CPC) needs its cents; whole counts do not.
  if (format === 'money') {
    return `${prefix}${value.toLocaleString(undefined, {
      maximumFractionDigits: abs < 10 ? 2 : 0,
    })}`;
  }
  return `${prefix}${value.toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })}`;
}

/** "Jul 3" — compact axis/table date from an ISO YYYY-MM-DD. */
export function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  // Constructed as a LOCAL date on purpose: `new Date('2026-07-03')` parses as
  // UTC midnight and renders as Jul 2 for anyone west of Greenwich.
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}
